import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert } from '@mui/material';
import {
  getPublicRegisterModules,
  getPublicRegisterOptions,
  previewPublicRegister,
  previewPublicRegisterSlug,
  submitPublicTrialRegistration,
} from '../api/publicRegistrationApi';
import { PublicOnboardingShell } from '../components/PublicOnboardingShell';
import { RegisterWizardHeader } from '../components/RegisterWizardHeader';
import { RegisterStepCompany } from '../components/register/RegisterStepCompany';
import { RegisterStepModules } from '../components/register/RegisterStepModules';
import { RegisterStepReview } from '../components/register/RegisterStepReview';
import { RegisterStepTenant } from '../components/register/RegisterStepTenant';
import { RegisterWizardFooter } from '../components/register/RegisterWizardFooter';
import { REGISTER_WIZARD_MAX_WIDTH } from '../components/register/registerSurfaces';
import { resolvePhonePrefixCountryCode } from '../utils/countryDisplay';
import {
  FALLBACK_REGISTER_OPTIONS,
  findCountryByCode,
  isValidNationalPhoneInput,
} from '../utils/registerCustomerForm';
import {
  PRE_SUBMIT_STEPS,
  STEP_META,
  buildReadyChecklist,
  buildRegisterCustomerPayload,
  canSubmitRegistration,
  clearWizardDraft,
  earliestIncompleteStep,
  loadWizardDraft,
  parseStepParam,
  resolveRegisterBillingInterval,
  resolveTierFromUsersCount,
  saveLaunchContext,
  saveWizardDraft,
  usersCountForTier,
  isConsumerCustomer,
  resolveSlugSourceName,
  validateCompanyStep,
  validateModulesStep,
  validateTenantStep,
} from '../utils/registerWizardModel';

function parseModulesParam(value) {
  if (!value) {
    return [];
  }
  return value.split(',').map((code) => code.trim()).filter(Boolean);
}

function resolveInitialUsersCount(searchParams) {
  const fromUrl = searchParams.get('users_count')
    || searchParams.get('users')
    || searchParams.get('seats');
  if (fromUrl) {
    const parsed = Number(fromUrl);
    if (!Number.isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return 10;
}

function urlSpecifiesUsers(searchParams) {
  return searchParams.get('users') !== null
    || searchParams.get('users_count') !== null
    || searchParams.get('seats') !== null;
}

function resolveInitialBilling(searchParams) {
  if (searchParams.get('billing') === null) {
    return 'monthly';
  }
  return resolveRegisterBillingInterval(searchParams.get('billing'));
}

/**
 * Mafert.sk links seed modules (and billing) without step=.
 * That prepopulates Step 1. It must not count as completing Step 1.
 */
function isUrlSeededModuleEntry(searchParams) {
  return searchParams.get('modules') !== null && !parseStepParam(searchParams.get('step'));
}

function stepAccessIndex(stepKey) {
  const idx = PRE_SUBMIT_STEPS.indexOf(stepKey);
  return idx >= 0 ? idx : 0;
}

function readInitialDraft() {
  return loadWizardDraft();
}

/**
 * Public six-step register wizard (Figma: Trial & Onboarding / Current).
 * Steps 1–4 live here; steps 5–6 are served by PublicRegisterLaunchStatusPage.
 * This component owns state, API calls and step routing only — every visual
 * block is a shared component under components/register.
 */
export function PublicRegisterPage() {
  const { t, i18n } = useTranslation(['onboarding', 'auth', 'common', 'pricing', 'register']);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDraftRef = useRef(readInitialDraft());
  const skipDraftSaveRef = useRef(false);
  const [idempotencyKey] = useState(() => (
    (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `reg_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
  ));

  const [catalogModules, setCatalogModules] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState('');

  const initialDraft = initialDraftRef.current;
  const urlHasModules = searchParams.get('modules') !== null;
  const urlHasBilling = searchParams.get('billing') !== null;
  const urlHasUsers = urlSpecifiesUsers(searchParams);
  const initialUsers = urlHasUsers
    ? resolveInitialUsersCount(searchParams)
    : (initialDraft?.usersCount ?? resolveInitialUsersCount(searchParams));
  const [selectedModules, setSelectedModules] = useState(() => {
    if (urlHasModules) {
      return parseModulesParam(searchParams.get('modules'));
    }
    if (initialDraft?.selectedModules?.length) {
      return initialDraft.selectedModules;
    }
    return [];
  });
  const [billingInterval, setBillingInterval] = useState(() => {
    if (urlHasBilling) {
      return resolveInitialBilling(searchParams);
    }
    return initialDraft?.billingInterval === 'yearly' ? 'yearly' : 'monthly';
  });
  const [usersCount, setUsersCount] = useState(initialUsers);
  const [userTierId, setUserTierId] = useState(() => (
    urlHasUsers
      ? resolveTierFromUsersCount(initialUsers)
      : (initialDraft?.userTierId || resolveTierFromUsersCount(initialUsers))
  ));
  const [smsNotificationsEnabled, setSmsNotificationsEnabled] = useState(
    () => initialDraft?.smsNotificationsEnabled === true,
  );
  const [startMode, setStartMode] = useState(() => initialDraft?.startMode || 'trial');
  const [buyNowEnabled, setBuyNowEnabled] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(
    () => initialDraft?.consentAccepted === true,
  );
  // Never restored from the draft: the final confirmation must be given again
  // every time the user reaches step 4.
  const [confirmationAccepted, setConfirmationAccepted] = useState(false);
  const [confirmationError, setConfirmationError] = useState(false);

  const modulesParam = useMemo(() => selectedModules.join(','), [selectedModules]);

  const queryContext = useMemo(() => ({
    modules: modulesParam,
    billing: billingInterval,
    users_count: usersCount,
    sms_notifications_enabled: smsNotificationsEnabled,
    start_mode: startMode,
    utm_source: searchParams.get('utm_source') || undefined,
    utm_medium: searchParams.get('utm_medium') || undefined,
    utm_campaign: searchParams.get('utm_campaign') || undefined,
    utm_term: searchParams.get('utm_term') || undefined,
    utm_content: searchParams.get('utm_content') || undefined,
    source: searchParams.get('source') || 'public_register',
    landing_url: typeof window !== 'undefined' ? window.location.href : undefined,
    referrer_url: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
  }), [searchParams, modulesParam, billingInterval, usersCount, smsNotificationsEnabled, startMode]);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [stepErrors, setStepErrors] = useState({});

  const [form, setForm] = useState(() => ({
    customer_type: '',
    company_name: '',
    contact_name: '',
    contact_email: '',
    country_code: FALLBACK_REGISTER_OPTIONS.default_country_code,
    country_name: '',
    phone_country_code: FALLBACK_REGISTER_OPTIONS.default_phone_country_code,
    phone_national_number: '',
    locale: FALLBACK_REGISTER_OPTIONS.default_locale,
    timezone: FALLBACK_REGISTER_OPTIONS.default_timezone,
    billing_currency: FALLBACK_REGISTER_OPTIONS.default_billing_currency,
    company_id_number: '',
    tax_id: '',
    vat_id: '',
    address_line1: '',
    city: '',
    postal_code: '',
    tenant_name: '',
    tenant_slug: '',
    primary_domain: '',
    notes: '',
    ...(initialDraft?.form && typeof initialDraft.form === 'object' ? initialDraft.form : {}),
  }));
  const [registerOptions, setRegisterOptions] = useState(FALLBACK_REGISTER_OPTIONS);
  const [optionsWarning, setOptionsWarning] = useState(false);
  const [phonePrefixManuallyEdited, setPhonePrefixManuallyEdited] = useState(
    () => initialDraft?.phonePrefixManuallyEdited === true,
  );
  const [phoneValidationError, setPhoneValidationError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [slugPreview, setSlugPreview] = useState(null);
  const [slugPreviewLoading, setSlugPreviewLoading] = useState(false);
  const [slugPreviewError, setSlugPreviewError] = useState(false);
  const [advancedEdited, setAdvancedEdited] = useState(() => ({
    tenant_name: false,
    tenant_slug: false,
    primary_domain: false,
    ...(initialDraft?.advancedEdited || {}),
  }));
  // Latest advanced-edit flags for in-flight slug-preview applies (avoid stale closure).
  const advancedEditedRef = useRef(advancedEdited);
  advancedEditedRef.current = advancedEdited;
  // Monotonic id so an older in-flight preview never overwrites a newer one.
  const slugPreviewRequestIdRef = useRef(0);

  const wizardState = useMemo(() => ({
    selectedModules,
    form,
    consentAccepted,
    tenantSlug: form.tenant_slug,
  }), [selectedModules, form, consentAccepted]);

  const allowedStep = useMemo(
    () => earliestIncompleteStep(wizardState),
    [wizardState],
  );

  const urlStepRaw = searchParams.get('step');
  const urlStep = parseStepParam(urlStepRaw);
  const seededModuleEntry = isUrlSeededModuleEntry(searchParams);

  const currentStep = useMemo(() => {
    if (seededModuleEntry) {
      return 'modules';
    }
    const resolved = urlStep || allowedStep;
    if (stepAccessIndex(resolved) > stepAccessIndex(allowedStep)) {
      return allowedStep;
    }
    return resolved;
  }, [seededModuleEntry, urlStep, allowedStep]);

  const setStep = useCallback((stepKey) => {
    if (stepKey !== 'review') {
      setConfirmationAccepted(false);
      setConfirmationError(false);
    }
    const next = new URLSearchParams(searchParams);
    next.set('step', stepKey);
    if (selectedModules.length > 0) {
      next.set('modules', selectedModules.join(','));
    } else {
      next.delete('modules');
    }
    next.set('billing', billingInterval);
    next.set('users', String(usersCount));
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, selectedModules, billingInterval, usersCount]);

  useEffect(() => {
    if (seededModuleEntry) {
      setStep('modules');
      return;
    }
    const needsRedirect = !urlStep
      || stepAccessIndex(urlStep) > stepAccessIndex(allowedStep);
    if (needsRedirect && currentStep !== urlStep) {
      setStep(currentStep);
    }
  }, [seededModuleEntry, urlStep, allowedStep, currentStep, setStep]);

  // H05H contract: modules / billing / users stay seedable from the URL.
  // Params are only applied when present so a restored draft is not wiped by a
  // bare /register?step=... link.
  useEffect(() => {
    if (searchParams.get('modules') !== null) {
      const fromUrl = parseModulesParam(searchParams.get('modules'));
      setSelectedModules((prev) => (prev.join(',') === fromUrl.join(',') ? prev : fromUrl));
    }
    if (searchParams.get('billing') !== null) {
      const nextBilling = resolveRegisterBillingInterval(searchParams.get('billing'));
      setBillingInterval((prev) => (prev === nextBilling ? prev : nextBilling));
    }
    if (searchParams.get('users') !== null
      || searchParams.get('users_count') !== null
      || searchParams.get('seats') !== null) {
      const nextUsers = resolveInitialUsersCount(searchParams);
      setUsersCount((prev) => (Number(prev) === Number(nextUsers) ? prev : nextUsers));
      setUserTierId((prev) => {
        const nextTier = resolveTierFromUsersCount(nextUsers);
        return prev === nextTier ? prev : nextTier;
      });
    }
  }, [searchParams]);

  useEffect(() => {
    if (skipDraftSaveRef.current || submitting) {
      return;
    }
    saveWizardDraft({
      selectedModules,
      billingInterval,
      usersCount,
      userTierId,
      smsNotificationsEnabled,
      startMode,
      consentAccepted,
      form,
      advancedEdited,
      phonePrefixManuallyEdited,
    });
  }, [
    selectedModules,
    billingInterval,
    usersCount,
    userTierId,
    smsNotificationsEnabled,
    startMode,
    consentAccepted,
    form,
    advancedEdited,
    phonePrefixManuallyEdited,
    submitting,
  ]);

  useEffect(() => {
    setCatalogLoading(true);
    getPublicRegisterModules()
      .then(setCatalogModules)
      .catch(() => setCatalogError(t('auth:preview_load_failed')))
      .finally(() => setCatalogLoading(false));
  }, [t]);

  useEffect(() => {
    getPublicRegisterOptions()
      .then((options) => {
        setRegisterOptions(options);
        setOptionsWarning(false);
        const defaultCountry = findCountryByCode(options.countries, options.default_country_code);
        setForm((prev) => ({
          ...prev,
          country_code: options.default_country_code || prev.country_code,
          country_name: defaultCountry?.name || prev.country_name,
          phone_country_code: options.default_phone_country_code || prev.phone_country_code,
          locale: options.default_locale || prev.locale,
          timezone: options.default_timezone || prev.timezone,
          billing_currency: options.default_billing_currency || prev.billing_currency,
        }));
        setStartMode(options.default_start_mode || 'trial');
        setBuyNowEnabled(options.start_modes?.buy_now?.enabled === true);
      })
      .catch(() => {
        setRegisterOptions(FALLBACK_REGISTER_OPTIONS);
        setOptionsWarning(true);
        const defaultCountry = findCountryByCode(
          FALLBACK_REGISTER_OPTIONS.countries,
          FALLBACK_REGISTER_OPTIONS.default_country_code,
        );
        setForm((prev) => ({
          ...prev,
          country_code: FALLBACK_REGISTER_OPTIONS.default_country_code,
          country_name: defaultCountry?.name || '',
          phone_country_code: FALLBACK_REGISTER_OPTIONS.default_phone_country_code,
          locale: FALLBACK_REGISTER_OPTIONS.default_locale,
          timezone: FALLBACK_REGISTER_OPTIONS.default_timezone,
          billing_currency: FALLBACK_REGISTER_OPTIONS.default_billing_currency,
        }));
      });
  }, [i18n.language]);

  const syncUrlParams = useCallback((modules, billing, users) => {
    const next = new URLSearchParams(searchParams);
    if (modules.length > 0) {
      next.set('modules', modules.join(','));
    } else {
      next.delete('modules');
    }
    next.set('billing', billing);
    next.set('users', String(users));
    if (currentStep) {
      next.set('step', currentStep);
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, currentStep]);

  const loadPreview = useCallback(() => {
    setPreviewLoading(true);
    setPreviewError('');
    previewPublicRegister(queryContext)
      .then(setPreview)
      .catch(() => setPreviewError(t('auth:preview_load_failed')))
      .finally(() => setPreviewLoading(false));
  }, [queryContext, t]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  useEffect(() => {
    const identity = {
      customer_type: form.customer_type,
      company_name: form.company_name,
      contact_name: form.contact_name,
    };
    const slugSourceName = resolveSlugSourceName(identity);
    if (!slugSourceName) {
      setSlugPreview(null);
      setSlugPreviewError(false);
      return undefined;
    }
    const companyName = form.company_name.trim();
    const contactName = form.contact_name.trim();

    let cancelled = false;
    const timer = window.setTimeout(() => {
      const requestId = ++slugPreviewRequestIdRef.current;
      setSlugPreviewLoading(true);
      setSlugPreviewError(false);
      previewPublicRegisterSlug({
        company_name: companyName || undefined,
        // Consumer without company name: slug is derived from the contact name.
        contact_name: !companyName && isConsumerCustomer(identity) ? contactName : undefined,
        tenant_name: advancedEditedRef.current.tenant_name ? form.tenant_name : undefined,
        tenant_slug: advancedEditedRef.current.tenant_slug ? form.tenant_slug : undefined,
      })
        .then((data) => {
          if (cancelled || requestId !== slugPreviewRequestIdRef.current) {
            return;
          }
          const edited = advancedEditedRef.current;
          setSlugPreview(data);
          setForm((prev) => ({
            ...prev,
            tenant_name: edited.tenant_name ? prev.tenant_name : (data.tenant_name || ''),
            tenant_slug: edited.tenant_slug ? prev.tenant_slug : (data.slug || ''),
            primary_domain: edited.primary_domain ? prev.primary_domain : (data.primary_domain || ''),
          }));
        })
        .catch(() => {
          if (cancelled || requestId !== slugPreviewRequestIdRef.current) {
            return;
          }
          setSlugPreviewError(true);
        })
        .finally(() => {
          if (!cancelled && requestId === slugPreviewRequestIdRef.current) {
            setSlugPreviewLoading(false);
          }
        });
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    form.customer_type,
    form.company_name,
    form.contact_name,
    form.tenant_name,
    form.tenant_slug,
    advancedEdited.tenant_name,
    advancedEdited.tenant_slug,
    advancedEdited.primary_domain,
  ]);

  const clearStepError = useCallback((field) => {
    setStepErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const updateField = useCallback((field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (['tenant_name', 'tenant_slug', 'primary_domain'].includes(field)) {
      setAdvancedEdited((prev) => ({ ...prev, [field]: true }));
    }
    clearStepError(field);
  }, [clearStepError]);

  const updateCompanyName = useCallback((event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, company_name: value }));
    clearStepError('company_name');
  }, [clearStepError]);

  const handleCustomerTypeChange = useCallback((event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      customer_type: value,
      // Consumers never submit company identifiers; drop anything already typed.
      ...(value === 'consumer'
        ? { company_id_number: '', tax_id: '', vat_id: '' }
        : {}),
    }));
    clearStepError('customer_type');
    clearStepError('company_name');
  }, [clearStepError]);

  const handleCountryChange = useCallback((_event, country) => {
    if (!country) {
      return;
    }
    setForm((prev) => ({
      ...prev,
      country_code: country.code,
      country_name: country.name,
      phone_country_code: phonePrefixManuallyEdited
        ? prev.phone_country_code
        : (country.phone_code || prev.phone_country_code),
    }));
  }, [phonePrefixManuallyEdited]);

  const phonePrefixCountries = registerOptions.countries || FALLBACK_REGISTER_OPTIONS.countries;

  const handlePhonePrefixChange = (event) => {
    const country = findCountryByCode(phonePrefixCountries, event.target.value);
    if (!country) {
      return;
    }
    setPhonePrefixManuallyEdited(true);
    setForm((prev) => ({ ...prev, phone_country_code: country.phone_code }));
  };

  const handlePhoneNumberChange = (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, phone_national_number: value }));
    const invalid = !isValidNationalPhoneInput(value);
    setPhoneValidationError(invalid ? t('register:phoneInvalid') : '');
    if (!invalid) {
      clearStepError('phone_national_number');
    }
  };

  const handleConsentChange = (event) => {
    setConsentAccepted(event.target.checked);
    if (event.target.checked) {
      clearStepError('consent');
    }
  };

  const selectedCountry = useMemo(
    () => findCountryByCode(registerOptions.countries || [], form.country_code),
    [registerOptions.countries, form.country_code],
  );

  const phonePrefixCountryCode = useMemo(
    () => resolvePhonePrefixCountryCode(phonePrefixCountries, form.country_code, form.phone_country_code),
    [phonePrefixCountries, form.country_code, form.phone_country_code],
  );

  const toggleAdvanced = () => setShowAdvanced((prev) => !prev);

  const hasAdvancedEdits = advancedEdited.tenant_name
    || advancedEdited.tenant_slug
    || advancedEdited.primary_domain;

  /**
   * Payload fields are limited to what PublicTrialRegistrationRequest accepts.
   * Deferred on purpose: address_line2, legacy aliases (contact_phone, phone,
   * country) and seats.
   */
  const buildSubmitPayload = useCallback(() => {
    const payload = {
      ...queryContext,
      ...buildRegisterCustomerPayload(form, { selectedCountry, registerOptions }),
      users_count: Number(usersCount),
      sms_notifications_enabled: smsNotificationsEnabled,
      start_mode: startMode,
      idempotency_key: idempotencyKey,
    };

    if (showAdvanced && hasAdvancedEdits) {
      payload.tenant_name = form.tenant_name || undefined;
      payload.tenant_slug = form.tenant_slug || undefined;
      payload.primary_domain = form.primary_domain || undefined;
    } else if (form.tenant_slug.trim()) {
      payload.tenant_slug = form.tenant_slug.trim();
      payload.tenant_name = form.tenant_name.trim() || undefined;
      payload.primary_domain = form.primary_domain.trim() || undefined;
    }

    return payload;
  }, [
    queryContext,
    form,
    selectedCountry,
    registerOptions,
    usersCount,
    smsNotificationsEnabled,
    startMode,
    idempotencyKey,
    showAdvanced,
    hasAdvancedEdits,
  ]);

  const hasNoModules = selectedModules.length === 0;
  const canContinue = preview?.can_continue === true;

  const readyChecklist = useMemo(() => buildReadyChecklist({
    selectedModules,
    form,
    consentAccepted,
    pricingReady: canContinue,
  }), [selectedModules, form, consentAccepted, canContinue]);

  const submitEnabled = canSubmitRegistration({
    checklist: readyChecklist,
    confirmationAccepted,
    submitting,
  });

  /** The single POST boundary: step 4 → step 5. No other step calls it. */
  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    if (currentStep !== 'review' || submitting) {
      return;
    }
    if (!confirmationAccepted) {
      setConfirmationError(true);
      return;
    }
    setSubmitting(true);
    setSubmitError('');

    if (form.phone_national_number.trim() && !isValidNationalPhoneInput(form.phone_national_number)) {
      setPhoneValidationError(t('register:phoneInvalid'));
      setSubmitting(false);
      return;
    }

    const tenantErrors = validateTenantStep({ form });
    if (Object.keys(tenantErrors).length > 0) {
      setStepErrors(tenantErrors);
      setSubmitting(false);
      setStep('tenant');
      return;
    }

    try {
      const response = await submitPublicTrialRegistration(buildSubmitPayload());
      const launchId = response?.public_id || response?.launch?.public_id;
      if (launchId) {
        skipDraftSaveRef.current = true;
        saveLaunchContext(launchId, {
          modules: preview?.selected_module_codes?.length
            ? preview.selected_module_codes
            : selectedModules,
          billingInterval,
          usersCount: Number(usersCount),
          smsNotificationsEnabled,
          startMode,
          customerType: form.customer_type,
          companyName: form.company_name || form.contact_name,
          contactEmail: form.contact_email,
          tenantSlug: form.tenant_slug,
          primaryDomain: form.primary_domain,
        });
        clearWizardDraft();
        if (response?.retry_capability && typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(`h05f_retry_${launchId}`, response.retry_capability);
        }
        navigate(`/register/status/${launchId}`);
        return;
      }
      setSubmitError(t('auth:trial_registration_failed'));
    } catch (err) {
      const message = err?.response?.data?.message
        || err?.response?.data?.errors?.phone_national_number?.[0]
        || err?.response?.data?.errors?.phone?.[0]
        || err?.response?.data?.errors?.customer_type?.[0]
        || err?.response?.data?.errors?.company_name?.[0]
        || err?.response?.data?.errors?.modules?.[0]
        || err?.response?.data?.errors?.tenant_slug?.[0]
        || err?.response?.data?.errors?.contact_email?.[0]
        || err?.response?.data?.errors?.idempotency_key?.[0]
        || t('auth:trial_registration_failed');
      setSubmitError(message);
    } finally {
      if (skipDraftSaveRef.current) {
        clearWizardDraft();
      }
      setSubmitting(false);
    }
  };

  const toggleModule = (publicCode) => {
    setSelectedModules((prev) => {
      const next = prev.includes(publicCode)
        ? prev.filter((code) => code !== publicCode)
        : [...prev, publicCode];
      syncUrlParams(next, billingInterval, usersCount);
      return next;
    });
    clearStepError('modules');
  };

  const handleBillingChange = (event) => {
    const nextBilling = event.target.value;
    setBillingInterval(nextBilling);
    syncUrlParams(selectedModules, nextBilling, usersCount);
  };

  const handleTierChange = (event) => {
    const tierId = event.target.value;
    setUserTierId(tierId);
    const count = usersCountForTier(tierId);
    setUsersCount(count);
    syncUrlParams(selectedModules, billingInterval, count);
  };

  const validateCurrentStep = useCallback(() => {
    if (currentStep === 'modules') {
      return validateModulesStep({ selectedModules });
    }
    if (currentStep === 'company') {
      const phoneInvalid = form.phone_national_number.trim()
        && !isValidNationalPhoneInput(form.phone_national_number);
      return validateCompanyStep({ form, consentAccepted, phoneInvalid });
    }
    if (currentStep === 'tenant') {
      return validateTenantStep({ form });
    }
    return {};
  }, [currentStep, selectedModules, form, consentAccepted]);

  const goNext = () => {
    const errors = validateCurrentStep();
    if (Object.keys(errors).length > 0) {
      setStepErrors(errors);
      return;
    }
    setStepErrors({});
    const idx = PRE_SUBMIT_STEPS.indexOf(currentStep);
    if (idx >= 0 && idx < PRE_SUBMIT_STEPS.length - 1) {
      setStep(PRE_SUBMIT_STEPS[idx + 1]);
    }
  };

  const goBack = () => {
    setStepErrors({});
    const idx = PRE_SUBMIT_STEPS.indexOf(currentStep);
    if (idx > 0) {
      setStep(PRE_SUBMIT_STEPS[idx - 1]);
    }
  };

  const stepMeta = STEP_META[currentStep] || STEP_META.modules;
  const stepLabel = t('onboarding:register.wizard.step_label', {
    current: stepMeta.index,
    total: stepMeta.total,
  });

  const summaryProps = {
    preview,
    previewLoading,
    previewError,
    selectedModules,
    billingInterval,
    usersCount,
    smsNotificationsEnabled,
    startMode,
  };

  return (
    <PublicOnboardingShell
      maxWidth="md"
      maxWidthPx={REGISTER_WIZARD_MAX_WIDTH}
      testId="public-register-shell"
      showLoginAction
    >
      <RegisterWizardHeader
        stepKey={currentStep}
        title={t(`onboarding:register.wizard.${currentStep}_title`)}
        subtitle={t(`onboarding:register.wizard.${currentStep}_subtitle`)}
        stepLabel={stepLabel}
        wizardProgressLabel={t('onboarding:register.wizard.wizard_progress')}
      />

      {catalogError && currentStep === 'modules' ? (
        <Alert severity="error" sx={{ mb: 2 }}>{catalogError}</Alert>
      ) : null}
      {submitError && currentStep === 'review' ? (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="register-submit-error">
          {submitError}
        </Alert>
      ) : null}

      {currentStep === 'modules' ? (
        <RegisterStepModules
          catalogModules={catalogModules}
          catalogLoading={catalogLoading}
          onToggleModule={toggleModule}
          userTierId={userTierId}
          onTierChange={handleTierChange}
          onBillingChange={handleBillingChange}
          onSmsChange={(event) => setSmsNotificationsEnabled(event.target.checked)}
          onStartModeChange={(event) => setStartMode(event.target.value)}
          buyNowEnabled={buyNowEnabled}
          stepErrors={stepErrors}
          {...summaryProps}
        />
      ) : null}

      {currentStep === 'company' ? (
        <RegisterStepCompany
          form={form}
          stepErrors={stepErrors}
          optionsWarning={optionsWarning}
          registerOptions={registerOptions}
          selectedCountry={selectedCountry}
          phonePrefixCountries={phonePrefixCountries}
          phonePrefixCountryCode={phonePrefixCountryCode}
          phoneValidationError={phoneValidationError}
          consentAccepted={consentAccepted}
          onFieldChange={updateField}
          onCompanyNameChange={updateCompanyName}
          onCustomerTypeChange={handleCustomerTypeChange}
          onCountryChange={handleCountryChange}
          onPhonePrefixChange={handlePhonePrefixChange}
          onPhoneNumberChange={handlePhoneNumberChange}
          onConsentChange={handleConsentChange}
          findCountryByCode={findCountryByCode}
          {...summaryProps}
        />
      ) : null}

      {currentStep === 'tenant' ? (
        <RegisterStepTenant
          form={form}
          stepErrors={stepErrors}
          slugPreview={slugPreview}
          slugPreviewLoading={slugPreviewLoading}
          slugPreviewError={slugPreviewError}
          showAdvanced={showAdvanced}
          onToggleAdvanced={toggleAdvanced}
          onFieldChange={updateField}
          {...summaryProps}
        />
      ) : null}

      {currentStep === 'review' ? (
        <RegisterStepReview
          form={form}
          slugPreview={slugPreview}
          checklist={readyChecklist}
          confirmationAccepted={confirmationAccepted}
          confirmationError={confirmationError}
          onConfirmationChange={(event) => {
            setConfirmationAccepted(event.target.checked);
            if (event.target.checked) {
              setConfirmationError(false);
            }
          }}
          onEditModules={() => setStep('modules')}
          onEditCompany={() => setStep('company')}
          onEditTenant={() => setStep('tenant')}
          {...summaryProps}
        />
      ) : null}

      <RegisterWizardFooter
        mode={currentStep === 'review' ? 'submit' : 'next'}
        showBack={currentStep !== 'modules'}
        onBack={goBack}
        onNext={goNext}
        onSubmit={handleSubmit}
        nextDisabled={currentStep === 'modules' && (hasNoModules || !canContinue || catalogLoading)}
        submitDisabled={!submitEnabled}
        submitting={submitting}
      />
    </PublicOnboardingShell>
  );
}
